
@if (autopayEligibilityService.hasIneligibleAutopayPayables()) {
  <div class="alert alert--warning mt-4">
    {{ configService.customerName }} has restricted some types of invoices from automatic payment. Those invoices won’t be covered by AutoPay and need to be paid manually by their due date. Contact your firm for more details.
  </div>
} @else if (areMultipleBusinessEntitiesPresent()) {
  <div class="alert alert--warning mt-4">
    You have invoices from more than one business entity at {{ configService.customerName }}.
    Please note that you can only pay multiple invoices from the same business entity at a time.
  </div>
}

<div *ngIf="!configService.isMobile && (doUnpaidPayablesExist | async)" class="mt--100 data-table">
  <div class="data-table__head">
    <div class="grid-row grid-row--aic">
      <div class="grid-col">
        <h3>Unpaid</h3>
      </div>

      <div class="grid-col grid-col--auto">
        <button
          [disabled]="(selectedInvoicesWithAttachmentsCount$ | async) === 0"
          type="button"
          class="button button--text"
          data-cy="export-selected-invoices"
          (click)="invoicePdfService.exportMultipleInvoicesAsPDFs(selectedPayables())"
        >
          <svg
            viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"
            stroke-linecap="round" stroke-linejoin="round" class="button__icon"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span>Export {{ selectedInvoicesWithAttachmentsCount$ | async }} Invoices</span>
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="{ value: isPayWithInvoices$ | async } as showPayableSelections" class="data-table__cont" data-cy="unpaid-table">
    <table
      mat-table
      matSort
      [dataSource]="unpaidPayablesDataSource"
      matSortActive="entityName"
      matSortDirection="asc"
      class="data-table__tbl"
    >
      <ng-container matColumnDef="invoiceChecked">
        <th mat-header-cell *matHeaderCellDef class="padding-end-2">
          <mat-checkbox
            [checked]="showPayableSelections.value && (areAllVisiblePayablesSelected$ | async) === checkboxStates.ALL"
            [indeterminate]="showPayableSelections.value && (areAllVisiblePayablesSelected$ | async) === checkboxStates.SOME"
            (change)="onHeaderCheckboxChange($event)"
            data-cy="select-all-invoices"
          ></mat-checkbox>
        </th>

        <td mat-cell *matCellDef="let payable" class="padding-end-2" style="width: 1%">
          <div
            [tooltipEnabled]="payable.disabled"
            tooltip="You can only select and pay invoices from the same business entity at the same time."
          >
            <mat-checkbox
              [checked]="showPayableSelections.value && payable.checked"
              [disabled]="payable.disabled"
              data-cy="select-invoice"
              [attr.ar-tran-id]="payable.arTranId"
            ></mat-checkbox>
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="entityName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="evenly-padded-cell">Business Entity</th>
        <td mat-cell *matCellDef="let invoiceUi" class="evenly-padded-cell" style="width: 12.5%">{{ invoiceUi.entityName }}</td>
      </ng-container>

      <ng-container matColumnDef="clientName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="evenly-padded-cell">Account</th>
        <td mat-cell *matCellDef="let invoiceUi" class="evenly-padded-cell" style="width: 12.5%">{{ invoiceUi.clientName }}</td>
      </ng-container>

      <ng-container matColumnDef="invoiceNumber">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="padding-start-2">Invoice #</th>
        <td
          mat-cell
          *matCellDef="let invoiceUi"
          class="padding-start-2"
          style="width: 12.5%"
          data-cy="invoice-number"
        >{{ invoiceUi.invoiceNumber }}
        </td>
      </ng-container>

      <ng-container matColumnDef="dueDate">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Due</th>
        <td mat-cell *matCellDef="let invoiceUi" style="width: 15%">{{ invoiceUi.dueDateDisplay }}</td>
      </ng-container>

      <ng-container matColumnDef="amount">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Amount</th>
        <td mat-cell *matCellDef="let invoiceUi" style="width: 12.5%">
          {{ (invoiceUi.invoice.arTran.amount - invoiceUi.invoice.arTran.allocated) | currency : invoiceUi.currency?.code }}
        </td>
      </ng-container>

      <ng-container matColumnDef="displayStatus">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          Status
        </th>
        <td mat-cell *matCellDef="let invoiceUi" style="width: 20%">
          <div class="d-flex gap-2 flex-wrap">
            <ng-container *ngIf="invoiceUi.autoExecutionDate; else dueBadges">
              <div class="badge badge--info">
                <div
                  class="badge__label"
                  [tooltip]="'A partial payment will be made because a '+ (invoiceUi.maxAmount | currency) +' monthly AutoPay limit has been set for '+ invoiceUi.clientName +'.'"
                  [tooltipEnabled]="invoiceUi.partialPayment && invoiceUi.maxAmount"
                >
                  {{ invoiceUi.partialPayment ? (invoiceUi.amount | currency) + ' scheduled for AutoPay' : 'Scheduled for AutoPay' }}
                </div>

                <div class="badge__date">in {{ invoiceUi.autoPayDueIn }} day{{ invoiceUi.autoPayDueIn === 1 ? '' : 's' }}</div>
              </div>
            </ng-container>

            <ng-template #dueBadges>
              <div *ngIf="invoiceUi.overdue && invoiceUi.displayStatus !== DUE_UPON_RECEIPT">
                <div class="badge badge--error">
                  <div class="badge__label">Overdue</div>
                  <div class="badge__date">{{ invoiceUi.displayStatus }}</div>
                </div>
              </div>

              <div *ngIf="!invoiceUi.overdue && invoiceUi.displayStatus !== DUE_UPON_RECEIPT">
                <div class="badge badge--warning">
                  <div class="badge__label">Due</div>
                  <div class="badge__date">{{ invoiceUi.displayStatus }}</div>
                </div>
              </div>

              <div *ngIf="invoiceUi.displayStatus === DUE_UPON_RECEIPT" class="tc--xxdark">
                {{ DUE_UPON_RECEIPT }}
              </div>
            </ng-template>

            <!-- Alert if invoice will not be covered by autopay -->
            @if (
              invoiceUi.entityAutopayEnabled === false
              && autopayEligibilityService.autopayClientIdsWithIneligibleBusinessEntities().includes(invoiceUi.clientId)
              ) {
              <div class="badge badge--warning">
                <div class="badge__label d-flex align-items-center">
                  <aiwyn-icon name="info" tooltip="Invoice will not be covered by AutoPay."></aiwyn-icon>
                </div>
              </div>
            }
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="invoiceMenu">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let tableObject" class="width--1" (click)="$event.stopPropagation()">
          <button type="button" class="button button--sm data-table__more" [matMenuTriggerFor]="menu">
            <svg width="16px" height="16px" viewBox="0 0 16 16" class="button__icon">
              <circle fill="currentColor" cx="8" cy="8" r="2"></circle>
              <circle fill="currentColor" cx="2" cy="8" r="2"></circle>
              <circle fill="currentColor" cx="14" cy="8" r="2"></circle>
            </svg>
          </button>
          <mat-menu #menu="matMenu" xPosition="before">
            <div
              [tooltip]="tooltip"
              [tooltipEnabled]="tableObject.invoice.invoiceFileId == null"
            >
              <button
                mat-menu-item
                data-cy="preview-invoice"
                class="px-4"
                [disabled]="tableObject.invoice.invoiceFileId == null"
                (click)="invoicePdfService.previewInvoiceAsPDF(tableObject.invoice.arTran.id)"
              >
                Preview
              </button>
            </div>

            <div
              [tooltip]="tooltip"
              [tooltipEnabled]="tableObject.invoice.invoiceFileId == null"
            >
              <button
                mat-menu-item
                data-cy="export-invoice"
                class="px-4"
                [disabled]="tableObject.invoice.invoiceFileId == null"
                (click)="invoicePdfService.exportInvoiceAsPDF(tableObject.invoice.arTran.id, tableObject.invoice.invoiceNumber)"
              >
                Export
              </button>
            </div>

            <ng-template #tooltip>
              <p class="text-center">
                Invoice not available for download. Please contact {{ configService.customerName }} for more information.
              </p>
            </ng-template>
          </mat-menu>
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="columnsToDisplay" class="data-table__row"></tr>
      <tr
        mat-row *matRowDef="let row; columns: columnsToDisplay" (click)="selectRow(row, false)"
          class="data-table__row data-table__row--selectable"></tr>
    </table>
  </div>
</div>

<div *ngIf="configService.isMobile && (doUnpaidPayablesExist | async)">
  <div class="mt--200 grid-row grid-row--aic">
    <div class="grid-col">
      <h3>Unpaid</h3>
    </div>

    <div class="grid-col grid-col--auto">
      <button
        type="button"
        class="button button--simple"
        data-cy="export-selected-invoices"
        [disabled]="(selectedInvoicesWithAttachmentsCount$ | async) === 0"
        (click)="invoicePdfService.exportMultipleInvoicesAsPDFs(selectedPayables())"
      >
        <svg
          viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"
          stroke-linecap="round" stroke-linejoin="round" class="button__icon"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        <span>Export {{ selectedInvoicesWithAttachmentsCount$ | async }} Invoices</span>
      </button>
    </div>
  </div>

  <app-unpaid-mobile
    [everyInvoiceBelongsToSameBusinessEntity]="!areMultipleBusinessEntitiesPresent()"
    [invoices]="unpaidPayablesDataSource.data"
  ></app-unpaid-mobile>
</div>

<div *ngIf="unpaidPayablesDataSource.data.length === 0">
  @if (shouldShowInvoicesTableSummary$ | async) {
    <app-invoices-table-summary></app-invoices-table-summary>
  }

  <app-empty-page
    [title]="emptyPageService.invoiceTitle"
    [text]="configService.creditsEnabled ? emptyPageService.invoiceTextWithCredits : emptyPageService.invoiceText"
    (onPayWithoutInvoice)="onPayWithoutInvoice.emit()"
  ></app-empty-page>
</div>
